import { useCallback, useEffect, useState } from 'react';
import { loopbackPermission, probeDesktop, readDesktopSeenAt } from '@/services/desktopConnectionService';
import { readHandoffPreference } from '@/services/desktopNoteHandoffService';
import { useAuthStore } from '@/stores/authStore';
import { detectDesktopPlatform, isMobileDevice } from '@/utils/deviceDetection';

/** ISO-8601 time the person last dismissed (or acted on) the desktop app promo. */
export const DESKTOP_PROMO_DISMISSED_AT_KEY = 'notego.desktopPromo.dismissedAt';
const DISMISS_SNOOZE_MS = 14 * 24 * 60 * 60 * 1000;

export type DesktopPromoOs = 'mac' | 'windows';

export interface DesktopAppPromo {
  /** The OS to name in the copy, or null when the promo must not show. */
  os: DesktopPromoOs | null;
  /** Hides the promo and snoozes it for this browser. */
  dismiss: () => void;
}

function isPromoSnoozed(now = Date.now()): boolean {
  try {
    const raw = localStorage.getItem(DESKTOP_PROMO_DISMISSED_AT_KEY);
    const dismissedAt = raw ? Date.parse(raw) : NaN;
    return Number.isFinite(dismissedAt) && now - dismissedAt < DISMISS_SNOOZE_MS;
  } catch {
    // Unreadable storage: treat as snoozed rather than nag on every note.
    return true;
  }
}

function snoozePromo(): void {
  try {
    localStorage.setItem(DESKTOP_PROMO_DISMISSED_AT_KEY, new Date().toISOString());
  } catch {
    // The promo still hides for this page view.
  }
}

/**
 * Whether this browser has ever seen the desktop app: a loopback sighting
 * (any age) or a remembered "always open in desktop" choice.
 */
function hasLocalDesktopEvidence(): boolean {
  return readDesktopSeenAt() !== null || readHandoffPreference() === 'always';
}

/**
 * Decides whether to suggest the desktop app on a note that is still
 * processing. Pitching the app to someone who already has it is the failure
 * this guards against, so every signal must agree they don't:
 *
 * - `userData.desktopVersion` is empty — no desktop app has ever launched on
 *   this account, on any computer.
 * - This browser has no loopback sighting and no `always` handoff preference.
 * - A live loopback probe gets no answer. The probe only runs where it cannot
 *   raise Chrome's local-network permission prompt — popping that over the note
 *   for someone without the app is worse than skipping the probe — and a
 *   `denied` permission means installed-or-not is unknowable, so no promo.
 *
 * Plus: the computer is a Mac or Windows PC (never mobile), and the promo was
 * not dismissed in the last 14 days. The caller owns the plan gate
 * (`features.desktopDownload`) and folds it into `eligible`.
 *
 * `eligible` only opens the window; once shown, the promo stays until
 * dismissed even if processing finishes underneath it.
 */
export function useDesktopAppPromo(eligible: boolean): DesktopAppPromo {
  const userData = useAuthStore((state) => state.userData);
  const [os, setOs] = useState<DesktopPromoOs | null>(null);
  const [dismissed, setDismissed] = useState(false);

  const hasDesktopVersion = typeof userData?.desktopVersion === 'string' && userData.desktopVersion.trim() !== '';
  const ready = eligible && !!userData && !hasDesktopVersion;

  useEffect(() => {
    if (!ready || os !== null || dismissed) return;
    if (isMobileDevice() || isPromoSnoozed() || hasLocalDesktopEvidence()) return;

    const controller = new AbortController();
    void (async () => {
      const platform = await detectDesktopPlatform();
      if (controller.signal.aborted || platform.os === 'unknown') return;
      const permission = await loopbackPermission();
      if (controller.signal.aborted || permission === 'denied') return;
      if (permission !== 'prompt' && (await probeDesktop(controller.signal))) return;
      if (controller.signal.aborted) return;
      setOs(platform.os);
    })();
    return () => controller.abort();
  }, [ready, os, dismissed]);

  const dismiss = useCallback(() => {
    snoozePromo();
    setDismissed(true);
  }, []);

  return { os: dismissed ? null : os, dismiss };
}
